import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import usePageTitle from '../../../utils/hooks/use_page_title';
import { useRedirectOnWorkspaceChange } from '../../../utils/hooks/use_redirect_on_value_change';

import PipelineIcon from '../../common/icons/PipelineIcon';
import VersionIcon from '../../common/icons/VersionIcon';

import ButtonTabGroup from '../../common/ButtonTabGroup/ButtonTabGroup';
import HeadingLabel from '../../common/Labels/HeadingLabel';
import Icon from '../../common/icons/Icon';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import SubHeadingLabel from '../../common/Labels/SubHeadingLabel';

import PageLayout from '../../pages/PageLayout';
import PrimaryContent from '../../pages/layouts/PrimaryContent';
import SecondaryContent from '../../pages/layouts/SecondaryContent';

import ContentTags from '../../ContentTags/ContentTags';
import PageNotFound from '../../pages/PageNotFound';
import PipelineActivityLog from '../PipelineActivityLog/PipelineActivityLog';
import PipelineAnalyticsTab from './tabs/PipelineAnalyticsTab';
import PipelineDeploymentPanel from '../PipelineDeploymentPanel/PipelineDeploymentPanel';
import PipelineDetailTab from './tabs/PipelineDetailTab';
import PipelineInsightsTab from './tabs/PipelineInsightsTab';
import PipelineMetricsTab from './tabs/PipelineMetricsTab';
import PipelineStatusBadge from '../PipelineStatusBadge/PipelineStatusBadge';

import { PipelineDataProvider, usePipelineData } from '../PipelineDataContext';
import { AssayLoadingProvider } from '../../assays/AssayLoadingContext';
import { PipelineCommentsProvider } from '../../contexts/CommentData/CommentData';
import { TimeRangeProvider } from '../../contexts/TimeRangeContext/TimeRangeContext';
import useClientContext from '../../contexts/ClientData/ClientData';

export const PipelineDetailPage = ({
  pipeline,
  loading,
  error,
  isAnomalyDetectionEnabled,
  handleAddTag,
  handleDeleteTag,
}) => {
  const routeParams = useParams();
  const history = useHistory();

  const { areAssaysEnabled } = useClientContext();

  const PIPELINE_DETAIL_TABS = useMemo(() => ({
    Details: 'Details',
    Metrics: 'Metrics',
    ...(areAssaysEnabled ? { Insights: 'Insights' } : {}),
    Analytics: 'Analytics',
  }), [areAssaysEnabled]);

  const pageTabs = useMemo(() => {
    const options = Object.values(PIPELINE_DETAIL_TABS);
    return options.map((option) => {
      if (option === PIPELINE_DETAIL_TABS.Analytics) {
        return {
          value: option,
          disabled: !isAnomalyDetectionEnabled,
        }
      }
      return {
        value: option,
      }
    })
  }, [PIPELINE_DETAIL_TABS, isAnomalyDetectionEnabled]);

  const workspaceId = routeParams?.workspaceId;
  const pipelineId = routeParams?.id;
  const pipelineVersion = routeParams?.version;
  const selectedTabInUrl = routeParams?.selectedTab;

  const [selectedTab, setSelectedTab] = useState(routeParams?.selectedTab || 'details')
  const capitalizedTab = selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1);
  const { push: setRoute } = history;

  useEffect(() => {
    if (selectedTabInUrl !== selectedTab.toLowerCase()) {
      setRoute(`/workspaces/${workspaceId}/pipelines/${pipelineId}/versions/${pipelineVersion}/${selectedTab.toLowerCase()}`)
    }
  }, [setRoute, pipelineId, pipelineVersion, selectedTab, workspaceId, selectedTabInUrl])

  usePageTitle(`${pipeline ? pipeline.pipelineID : 'Pipeline'} | Wallaroo`);

  if (!loading && !error && !pipeline) {
    return <PageNotFound pipelineID={routeParams.id} pipelineVersion={routeParams.version} />;
  }

  const isDeployed = pipeline?.deployment?.deployed ?? false;

  return (
    <div className="flex flex-col w-full mb-16">
      <div className="bg-brand border-t-2 border-border-brand text-white flex justify-center items-center">
        <div className="px-16 py-8 w-full flex-grow flex flex-col items-start">
          <div className="flex items-center">
            <Icon icon={PipelineIcon} size={80} useTailwindSizing />
            <HeadingLabel className="ml-2 mt-1" size="4xl">
              {routeParams?.id}
            </HeadingLabel>
          </div>
          <div className="ml-12 mb-2 flex items-center">
            <Icon icon={VersionIcon} size={32} useTailwindSizing />
            <SubHeadingLabel className="ml-2" size="2xl">
              {routeParams?.version}
            </SubHeadingLabel>
          </div>
          {
            !pipeline ? (
              error ? (
                <SubHeadingLabel className="ml-2 mt-2" size="2xl">
                  Something went wrong loading this Pipeline
                </SubHeadingLabel>
              ) : (
                <div className="ml-2 mt-2 flex flex-row">
                  <SubHeadingLabel size="2xl">
                    Loading Pipeline Data
                  </SubHeadingLabel>
                  <LoadingSpinner className="ml-2" animationKey="pipeline-detail-loading-spinner" />
                </div>
              )
            ) : (
              <div className="ml-2 mt-2 flex items-center">
                <PipelineStatusBadge status={isDeployed ? 'Active' : 'Inactive'} />
                <div className="ml-2 w-4/5 text-brand">
                  <ContentTags
                    tags={pipeline.pipelineTags.map(({ tag }) => tag.tag)}
                    onAddTag={handleAddTag}
                    onDeleteTag={handleDeleteTag}
                  />
                </div>
              </div>
            )
          }
        </div>
      </div>
      {!error && (
        <TimeRangeProvider>
          <div className="my-4 px-16 ml-2">
            <ButtonTabGroup
              selectedOption={capitalizedTab}
              options={pageTabs}
              onItemSelected={setSelectedTab}
            />
          </div>
          <PageLayout>
            <PrimaryContent>
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Details ? (
                  <PipelineDetailTab />
                ) : null
              }
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Metrics ? (
                  <PipelineMetricsTab />
                ) : null
              }
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Analytics ? (
                  <PipelineAnalyticsTab />
                ) : null
              }
              {
                capitalizedTab === PIPELINE_DETAIL_TABS.Insights ? (
                  <PipelineInsightsTab />
                ) : null
              }
            </PrimaryContent>
            <SecondaryContent>
              <PipelineDeploymentPanel />
              <PipelineCommentsProvider pipeline={pipeline}>
                <PipelineActivityLog pipeline={pipeline} />
              </PipelineCommentsProvider>
            </SecondaryContent>
          </PageLayout>
        </TimeRangeProvider>
      )}
    </div>
  );
};

const ContextualPipelineDetailPage = (props) => {
  const {
    pipeline,
    loading,
    error,
    isAnomalyDetectionEnabled,
    addPipelineTag,
    deletePipelineTag,
  } = usePipelineData();

  useRedirectOnWorkspaceChange('/pipelines')

  return (
    <PipelineDetailPage
      pipeline={pipeline}
      loading={loading}
      error={error}
      isAnomalyDetectionEnabled={isAnomalyDetectionEnabled}
      handleAddTag={addPipelineTag}
      handleDeleteTag={deletePipelineTag}
      {...props}
    />
  )
}

const WrappedPipelineDetailPage = props => (
  <PipelineDataProvider>
    <AssayLoadingProvider>
      <ContextualPipelineDetailPage {...props} />
    </AssayLoadingProvider>
  </PipelineDataProvider>
)

export default WrappedPipelineDetailPage;
